/**
 * Shared React components for Ada business form.
 * Used by both AdaReviewBusinessInfo (registration flow) and AdaReviewModal (post-registration).
 */

import { Button, styled } from 'opensolar-ui'
import React from 'react'
import { SelectInput, TextInput } from 'react-admin'
import { OpenSolarPalette } from 'Themes'


export const SOCIAL_PLATFORM_CHOICES = [
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'twitter', name: 'Twitter / X' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'pinterest', name: 'Pinterest' },
  { id: 'yelp', name: 'Yelp' },
]


export const CardContainer = styled('div')({
  marginBottom: 12,
  border: `1px dashed ${OpenSolarPalette.greyLight2}`,
  borderRadius: 8,
  overflow: 'hidden',
})

export const CardFieldsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 16,
})

export const CardButtonsRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: 12,
  padding: '0 16px 16px 16px',
})

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the list of platforms that are already used by other social links.
 */
export const getUsedPlatforms = (fields: any, currentIndex: number): string[] => {
  const usedPlatforms: string[] = []
  fields.value?.forEach((item: any, idx: number) => {
    if (idx !== currentIndex && item?.platform) {
      usedPlatforms.push(item.platform)
    }
  })
  return usedPlatforms
}

export const getFirstAvailablePlatform = (usedPlatforms: string[]): string => {
  const availablePlatform = SOCIAL_PLATFORM_CHOICES.find((p) => !usedPlatforms.includes(p.id))
  return availablePlatform?.id || 'linkedin'
}

export const getAvailablePlatformChoices = (usedPlatforms: string[], currentPlatform?: string) => {
  return SOCIAL_PLATFORM_CHOICES.filter((p) => !usedPlatforms.includes(p.id) || p.id === currentPlatform)
}

// ============================================================================
// Social Link Card Component
// ============================================================================

export interface SocialLinkCardProps {
  name: string
  index: number
  fields: any
  translate: (key: string) => string
}

export const SocialLinkCard: React.FC<SocialLinkCardProps> = ({ name, index, fields, translate }) => {
  const fieldValue = fields.value[index]
  const platform = fieldValue?.platform || 'linkedin'
  const platformName = SOCIAL_PLATFORM_CHOICES.find((p) => p.id === platform)?.name || 'Social Link'

  const usedPlatforms = getUsedPlatforms(fields, index)
  const availableChoices = getAvailablePlatformChoices(usedPlatforms, platform)

  return (
    <CardContainer>
      <CardFieldsContainer>
        <SelectInput
          label={translate('Social Media Platform')}
          source={`${name}.platform`}
          choices={availableChoices}
          fullWidth
          variant="outlined"
        />
        <TextInput
          label={`${platformName} URL`}
          source={`${name}.url`}
          fullWidth
          variant="outlined"
          placeholder={`https://${platform}.com/...`}
        />
      </CardFieldsContainer>
      <CardButtonsRow>
        <Button onClick={() => fields.remove(index)} variant="outlined" color="error" type="button">
          {translate('Remove')}
        </Button>
      </CardButtonsRow>
    </CardContainer>
  )
}

// ============================================================================
// Customer Review Card Component
// ============================================================================

export interface CustomerReviewCardProps {
  name: string
  index: number
  fields: any
  translate: (key: string) => string
}

export const CustomerReviewCard: React.FC<CustomerReviewCardProps> = ({ name, index, fields, translate }) => {
  return (
    <CardContainer>
      <CardFieldsContainer>
        <TextInput
          label={"Review"}
          source={`${name}.text`}
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          placeholder={'John Doe:\nGreat service! Highly recommended.\nSource: Google'}
        />
      </CardFieldsContainer>
      <CardButtonsRow>
        <Button onClick={() => fields.remove(index)} variant="outlined" color="error" type="button">
          {translate('Remove')}
        </Button>
      </CardButtonsRow>
    </CardContainer>
  )
}

// ============================================================================
// Social Link Field Array Renderer
// ============================================================================

export interface SocialLinksFieldArrayProps {
  fields: any
  translate: (key: string) => string
}

export const renderSocialLinksFieldArray = ({ fields, translate }: SocialLinksFieldArrayProps) => {
  return (
    <>
      {fields.map((name: string, index: number) => (
        <SocialLinkCard key={name} name={name} index={index} fields={fields} translate={translate} />
      ))}
    </>
  )
}

// ============================================================================
// Customer Reviews Field Array Renderer
// ============================================================================

export interface CustomerReviewsFieldArrayProps {
  fields: any
  translate: (key: string) => string
}

export const renderCustomerReviewsFieldArray = ({ fields, translate }: CustomerReviewsFieldArrayProps) => {
  return (
    <>
      {fields.map((name: string, index: number) => (
        <CustomerReviewCard key={name} name={name} index={index} fields={fields} translate={translate} />
      ))}
    </>
  )
}
